import { Component, EnvironmentInjector, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ModalService } from '../../services/design/modal.service';
import { SnackbarService } from '../../services/design/snackbar.service';
import { UsersService } from '../../services/users/users.service';
import { UserDetail } from './userDetail/userDetail.component';
import { AppUserRole, assignedStaffRoles, roleLabel } from '../../core/auth/roles';

type RoleFilter = 'all' | AppUserRole;

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class Users implements OnInit {
  private usersService = inject(UsersService);
  private modalService = inject(ModalService);
  private envInjector = inject(EnvironmentInjector);
  private snackbar = inject(SnackbarService);

  users: any[] = [];
  loading = false;
  togglingId: string | null = null;

  page = 1;
  limit = 12;
  totalPages = 0;
  total = 0;
  activeCount = 0;
  roleCounts: Record<string, number> = {};

  sortBy = 'name';
  order: 'ASC' | 'DESC' = 'ASC';
  search = '';
  roleFilter: RoleFilter = 'all';

  readonly filters: { id: RoleFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'superadmin', label: 'Super Admin' },
    { id: 'store_manager', label: 'Store Manager' },
    { id: 'picker', label: 'Picker' },
    { id: 'inventory_operator', label: 'Inventory Operator' },
    { id: 'finance', label: 'Finance' },
    { id: 'rider', label: 'Rider' },
    { id: 'user', label: 'Customer' },
  ];

  ngOnInit(): void {
    this.loadUsers();
  }

  roleLabel = roleLabel;
  userRoles(user: { role: string; additional_roles?: string[] }): string[] {
    const roles=assignedStaffRoles(user.role,user.additional_roles);
    return roles.length ? roles : [user.role];
  }

  async loadUsers() {
    this.loading = true;
    try {
      const res = await this.usersService.getAllUsers(
        this.page,
        this.limit,
        this.search,
        this.sortBy,
        this.order,
        undefined,
        this.roleFilter === 'all' ? null : this.roleFilter
      );

      this.users = res.data;
      this.total = res.total;
      this.totalPages = res.totalPages;
      this.activeCount = res.activeCount;
      this.roleCounts = res.roleCounts;
    } catch (err: any) {
      this.snackbar.error(err?.message || 'Failed to load users');
    } finally {
      this.loading = false;
    }
  }

  setRoleFilter(filter: RoleFilter) {
    this.roleFilter = filter;
    this.page = 1;
    this.loadUsers();
  }

  onSearch() {
    this.page = 1;
    this.loadUsers();
  }

  onSort(column: string) {
    if (this.sortBy === column) {
      this.order = this.order === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortBy = column;
      this.order = 'ASC';
    }
    this.loadUsers();
  }

  nextPage() {
    if (this.page < this.totalPages) {
      this.page++;
      this.loadUsers();
    }
  }

  prevPage() {
    if (this.page > 1) {
      this.page--;
      this.loadUsers();
    }
  }

  editUser(user: any) {
    this.modalService
      .open(
        UserDetail,
        {
          user: {
            ...user,
            isEdit: true,
            storeId: user.store_id,
            originalRole: user.role,
            originalStoreId: user.store_id,
            staffRoles: assignedStaffRoles(user.role,user.additional_roles),
            originalStaffRoles: assignedStaffRoles(user.role,user.additional_roles),
            isActive: user.is_active,
            email: user.email || '',
          },
        },
        this.envInjector
      )
      .subscribe((result) => {
        if (result) this.loadUsers();
      });
  }

  addUser() {
    this.modalService
      .open(
        UserDetail,
        {
          user: {
            isEdit: false,
            name: '',
            email: '',
            phone: '',
            role: 'store_manager',
            isActive: true,
            storeId: undefined,
            password: '',
          },
        },
        this.envInjector
      )
      .subscribe((result) => {
        if (result) this.loadUsers();
      });
  }

  async toggleUserStatus(user: any) {
    this.togglingId = user.id;
    try {
      const newStatus = !user.is_active;
      await this.usersService.setUserActive(user.id, newStatus);
      user.is_active = newStatus;
      this.snackbar.success(
        newStatus ? 'Access restored' : 'Account disabled across all apps'
      );
      this.loadUsers();
    } catch (err: any) {
      this.snackbar.error(err.message || 'Failed to update user status');
    } finally {
      this.togglingId = null;
    }
  }

  getRoleBadgeClass(role: string): string {
    switch ((role || '').toLowerCase()) {
      case 'superadmin':
        return 'badge-error';
      case 'store_manager':
        return 'badge-primary';
      case 'rider':
        return 'badge-accent';
      case 'user':
        return 'badge-ghost';
      default:
        return 'badge-neutral';
    }
  }

  activeRate(): number {
    if (!this.total) return 0;
    return Math.round((this.activeCount / Math.max(this.total, 1)) * 100);
  }
}
